import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios, { AxiosError } from "axios";
import type { LabcorpOrder } from "src/components/staff/labs/order/labcorp/LabcorpOrder.schema";
import { ELabsRangeStatus, ELabsStatus, ELabsVendorType } from "src/types/global_enums";
import type { ExternalLabResult } from "src/types/models/hl7";
import { buildStaffResourcePathForCurrentUser } from "../ResourceBuilder";

export type StaffLabFileUpload = {
    id: number;
    fileName: string;
    signedUrl: string;
    lastUpdatedOnUnix: number;
};

export type StaffLabOrderDetail = {
    createdOn: string;
    lastUpdatedOn: string;
    snapshot: LabcorpOrder | Record<string, unknown>;
};

export type StaffLabTestComponentGlobal = {
    id: number;
    createdOn: string;
    lastUpdatedOn: string;
    name: string;
    cptCode: string;
    units: string;
    reviewNeeded: boolean;
    accessMedId: string;
};

export type StaffLabTestPacket = {
    id: number;
    createdOn: string;
    lastUpdatedOn: string;
    externalId: string;
    name: string;
    ladTestPanels: StaffLabTestPanel[];
    labTestComponents: StaffLabTestComponent[];
};

export type StaffLabTestPanel = {
    id: number;
    createdOn: string;
    lastUpdatedOn: string;
    externalId: string;
    name: string;
    labTestComponents: StaffLabTestComponent[];
};

export type StaffLabTestComponent = {
    id: number;
    createdOn: string;
    lastUpdatedOn: string;
    externalId: string;
    name: string;
    value?: number | null;
    units: string;
    rangeStatus: ELabsRangeStatus;
    comments: string;
    labTestComponentGlobal: StaffLabTestComponentGlobal;
};

export type StaffLab = {
    id: number;
    completedOn: number;
    createdOn: number;
    lastUpdatedOn: number;
    labFileUploads: StaffLabFileUpload[];
    manual: boolean;
    labOrderDetail?: StaffLabOrderDetail;
    accountPatientId: number;
    appointmentId: number;
    locationId: number;
    notes?: string;
    drawnById?: number;
    labTestPackets?: StaffLabTestPacket[];
    labTestPanels?: StaffLabTestPanel[];
    labTestComponents?: StaffLabTestComponent[];
    otherVendorName?: string;
    labMachineId?: number;
    status: ELabsStatus;
    labVendorLabel: string;
    locked: boolean;
    accountPatientName: string;
    appointmentType?: string;
    appointmentDate: string;
    appointmentLocationName: string;
    labVendorType: ELabsVendorType;
};

const staffLabsApi = createApi({
    reducerPath: "lobbie/staff/labs",
    baseQuery: fetchBaseQuery({}),
    endpoints: (builder) => ({
        getLab: builder.query<StaffLab, number>({
            queryFn: async (labId) => {
                const url = buildStaffResourcePathForCurrentUser(`labs/${labId}`);
                try {
                    const response = await axios.get<StaffLab>(url, { withCredentials: true });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
        }),
        getLabVendor: builder.query<string, number>({
            queryFn: async (labId) => {
                const url = buildStaffResourcePathForCurrentUser(`labs/${labId}/vendor`);
                try {
                    const response = await axios.get<string>(url, { withCredentials: true });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
        }),
        getExternalResults: builder.query<ExternalLabResult, number>({
            queryFn: async (labId) => {
                const url = buildStaffResourcePathForCurrentUser(`labs/external_results/${labId}`);
                try {
                    const response = await axios.get<ExternalLabResult>(url, {
                        withCredentials: true,
                    });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
        }),
        getLabsForAppointment: builder.query<StaffLab[], number>({
            queryFn: async (appointmentId) => {
                const url = buildStaffResourcePathForCurrentUser(
                    `appointments/${appointmentId}/labs`,
                );
                try {
                    const response = await axios.get<StaffLab[]>(url, { withCredentials: true });
                    return { data: response.data };
                } catch (error) {
                    const axiosError = error as AxiosError;
                    return {
                        data: undefined,
                        error: {
                            status: axiosError.status ?? 0,
                            data: axiosError.response?.data,
                        },
                    };
                }
            },
        }),
    }),
});

export const {
    useGetLabQuery,
    useGetLabVendorQuery,
    useLazyGetLabQuery,
    useLazyGetLabVendorQuery,
    useGetExternalResultsQuery,
    useLazyGetExternalResultsQuery,
    useGetLabsForAppointmentQuery,
    useLazyGetLabsForAppointmentQuery,
} = staffLabsApi;

export default staffLabsApi;
